'use client';

import { CheckboxInput } from './inputs/CheckboxInput';
import { NumberInput } from './inputs/NumberInput';
import { SelectInput } from './inputs/SelectInput';
import { TextInput } from './inputs/TextInput';
import { useInputLogic } from './useInput';

import type {
  TextInputProps,
  NumberInputProps,
  CheckboxInputProps,
  SelectInputProps,
  SelectOption,
} from './types';
import type { KeyboardEvent } from 'react';
import type { ZodType } from 'zod';

type CommonProps = {
  name: string;
  label?: string;
  error?: string;
  readOnly?: boolean;
};

type SmartTextProps = CommonProps & {
  type: 'text' | 'password' | 'textarea';
  size?: 'small' | 'medium';
  minRows?: number;
  maxRows?: number;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  onValidateChange?: (value: string, error: string | null) => void;
  schema?: ZodType<string>;
};

type SmartNumberProps = CommonProps & {
  type: 'number';
  value?: number | null;
  defaultValue?: number | null;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number | null) => void;
  onBlur?: () => void;
  onValidateChange?: (value: number | null, error: string | null) => void;
  schema?: ZodType<number | null>;
  formatOnBlur?: boolean;
  asCurrency?: boolean;
  locale?: string;
  currency?: string;
};

type SmartCheckboxProps = CommonProps & {
  type: 'checkbox';
  checked?: boolean;
  defaultChecked?: boolean;
  checkboxColorVariant?: 'primary' | 'green';
  ariaLabel?: string;
  onChange?: (checked: boolean) => void;
  onValidateChange?: (checked: boolean, error: string | null) => void;
  schema?: ZodType<boolean>;
};

export type SmartSelectProps<T = string | number> = CommonProps & {
  type: 'select';
  options: SelectOption<T>[];
  value?: T;
  defaultValue?: T;
  size?: 'small' | 'medium';
  onChange?: (value: T) => void;
  onValidateChange?: (value: T, error: string | null) => void;
  schema?: ZodType<T>;
};

export type SmartInputProps =
  | SmartTextProps
  | SmartNumberProps
  | SmartCheckboxProps
  | SmartSelectProps<any>;

export function SmartInput(props: SmartInputProps) {
  switch (props.type) {
    case 'checkbox': {
      return <SmartCheckboxInput {...props} />;
    }
    case 'number': {
      return <SmartNumberInput {...props} />;
    }
    case 'select': {
      return <SmartSelectInput {...props} />;
    }
    case 'text':
    case 'password':
    case 'textarea':
    default: {
      return <SmartTextInput {...props} />;
    }
  }
}

function SmartCheckboxInput(props: SmartCheckboxProps) {
  const { internalValue, handleChange, errorText } = useInputLogic<boolean>({
    value: props.checked,
    defaultValue: props.defaultChecked ?? false,
    onChange: props.onChange,
    onValidateChange: props.onValidateChange,
    schema: props.schema,
  });

  const inputProps: CheckboxInputProps = {
    name: props.name,
    label: props?.label ?? '',
    error: errorText || props.error,
    checked: Boolean(internalValue),
    colorVariant: props.checkboxColorVariant ?? 'primary',
    ariaLabel: props.ariaLabel,
    onChange: handleChange,
    readOnly: props.readOnly,
  };

  return <CheckboxInput {...inputProps} />;
}

function SmartNumberInput(props: SmartNumberProps) {
  const { internalValue, errorText, handleChange } = useInputLogic<
    number | null
  >({
    value: props.value,
    defaultValue: props.defaultValue ?? null,
    onChange: props.onChange,
    onValidateChange: props.onValidateChange,
    schema: props.schema,
  });

  const inputProps: NumberInputProps = {
    name: props.name,
    label: props?.label ?? '',
    error: errorText || props.error,
    value: internalValue,
    min: props.min,
    max: props.max,
    step: props.step,
    onChange: handleChange,
    onBlur: props.onBlur,
    readOnly: props.readOnly,
    formatOnBlur: props.formatOnBlur,
    asCurrency: props.asCurrency,
    locale: props.locale,
    currency: props.currency,
  };

  return <NumberInput {...inputProps} />;
}

function SmartSelectInput(props: SmartSelectProps<any>) {
  const { internalValue, errorText, handleChange } = useInputLogic<any>({
    value: props.value,
    defaultValue: props.defaultValue,
    onChange: props.onChange,
    onValidateChange: props.onValidateChange,
    schema: props.schema as ZodType<any> | undefined,
  });

  const inputProps: SelectInputProps<any> = {
    name: props.name,
    label: props?.label ?? '',
    error: errorText || props.error,
    value: internalValue,
    options: props.options,
    size: props.size,
    onChange: handleChange,
    readOnly: props.readOnly,
  };

  return <SelectInput {...inputProps} />;
}

function SmartTextInput(props: SmartTextProps) {
  const { internalValue, errorText, handleChange } = useInputLogic<string>({
    value: props.value,
    defaultValue: props.defaultValue ?? '',
    onChange: props.onChange,
    onValidateChange: props.onValidateChange,
    schema: props.schema,
  });

  const inputProps: TextInputProps & {
    type: 'text' | 'password' | 'textarea';
  } = {
    type: props.type,
    name: props.name,
    label: props?.label ?? '',
    error: errorText || props.error,
    value: internalValue,
    size: props.size,
    onChange: handleChange,
    readOnly: props.readOnly,
    minRows: props.minRows,
    maxRows: props.maxRows,
    onBlur: props.onBlur,
    onKeyDown: props.onKeyDown,
    autoFocus: props.autoFocus,
  };

  return <TextInput {...inputProps} />;
}

/**
 * * ────────────────────────────────────────────────────────────────────
 * * EXAMPLES — How to instantiate every SmartInput type with all props.
 * * ────────────────────────────────────────────────────────────────────
 */

//  {
// *   /* TEXT INPUT */
//  }

// * <SmartInput
// *   type="text"
// *   name="username"
// *   label="Username"
// *   value={form.username}
// *   onChange={(v) => setForm((f) => ({ ...f, username: v }))}
// *   onValidateChange={(v, err) => console.log('validated text:', v, err)}
// *   error={errors.username}
// *   defaultValue=""
// *   schema={textSchema}
// * />;

//  {
// *   /* PASSWORD INPUT */
//  }

// * <SmartInput
// *   type="password"
// *   name="password"
// *   label="Password"
// *   value={form.password}
// *   onChange={(v) => setForm((f) => ({ ...f, password: v }))}
// *   onValidateChange={(v, err) => console.log('validated password:', v, err)}
// *   error={errors.password}
// *   defaultValue=""
// *   schema={passwordSchema}
// * />;

//  {
// *   /* TEXTAREA INPUT */
//  }

// * <SmartInput
// *   type="textarea"
// *   name="bio"
// *   label="Biography"
// *   value={form.bio}
// *   onChange={(v) => setForm((f) => ({ ...f, bio: v }))}
// *   onValidateChange={(v, err) => console.log('validated textarea:', v, err)}
// *   error={errors.bio}
// *   defaultValue=""
// *   schema={bioSchema}
// * />;

//  {
// *   /* NUMBER INPUT */
//  }

// * <SmartInput
// *   type="number"
// *   name="age"
// *   label="Age"
// *   value={form.age}
// *   onChange={(v) => setForm((f) => ({ ...f, age: v }))}
// *   onValidateChange={(v, err) => console.log('validated number:', v, err)}
// *   error={errors.age}
// *   min={0}
// *   max={120}
// *   step={1}
// *   defaultValue={null}
// *   schema={ageSchema}
// * />;

//  {
// *   /* CHECKBOX INPUT */
//  }

// * <SmartInput
// *   type="checkbox"
// *   name="newsletter"
// *   label="Join newsletter?"
// *   checked={form.newsletter}
// *   onChange={(v) => setForm((f) => ({ ...f, newsletter: v }))}
// *   onValidateChange={(v, err) => console.log('validated checkbox:', v, err)}
// *   error={errors.newsletter}
// * />;

//  {
// *   /* SELECT INPUT */
//  }

// * <SmartInput
// *   type="select"
// *   name="country"
// *   label="Country"
// *   value={form.country}
// *   onChange={(v) => setForm((f) => ({ ...f, country: v }))}
// *   options={[
// *     { value: 'ar', label: 'Argentina' },
// *     { value: 'us', label: 'United States' },
// *     { value: 'de', label: 'Germany' },
// *   ]}
// *   onValidateChange={(v, err) => console.log('validated select:', v, err)}
// *   error={errors.country}
// *   defaultValue="ar"
// *   schema={countrySchema}
// * />;
